/*
 * File: auth.service.tsx
 * Project: a-a
 * File Created: Tuesday, 15th October 2024 8:15:14 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Monday, 15th September 2025 8:28:28 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import { head } from 'aws-amplify/api';
import axios from '../core/axios';
import {
  IChangePasswordPayload,
  ILoginPayload,
  ILogoutPayload,
  IRegisterPayload,
  ISetPasswordPayload,
  IUserNamePayload,
} from '../model/auth.model';
import { ApiConstants } from './api-constants';

export class AuthService {
  static refreshToken = async (refreshToken: string, identityToken: string) => {
    return await (
      await axios.post(ApiConstants.AUTH_API.REFRESH.URL(), { refreshToken, identityToken })
    ).data;
  };
  static validateUser = async (id: string) => {
    return await (
      await axios.get(ApiConstants.AUTH_API.VALIDATE.URL(id))
    ).data;
  };
  static resendConfirm = async (payload: IUserNamePayload) => {
    return await (
      await axios.post(ApiConstants.AUTH_API.RESEND_CONFIRMATION.URL(), payload)
    ).data;
  };
  static register = async (payload: IRegisterPayload) => {
    return await (
      await axios.post(ApiConstants.AUTH_API.CREATE_USER.URL(), payload)
    ).data;
  };
  static setPassword = async (payload: ISetPasswordPayload) => {
    return await (
      await axios.post(ApiConstants.AUTH_API.SET_PASSWORD.URL(), payload)
    ).data;
  };
  static forgotPassword = async (payload: IUserNamePayload) => {
    return await (
      await axios.post(ApiConstants.AUTH_API.FORGOT_PASSWORD.URL(), payload)
    ).data;
  };
  static changePassword = async (payload: IChangePasswordPayload) => {
    return await (
      await axios.post(ApiConstants.AUTH_API.CHANGE_PASSWORD.URL(), payload)
    ).data;
  };
  static login = async (payload: ILoginPayload) => {
    try {
      return await (
        await axios.post(ApiConstants.AUTH_API.LOGIN.URL(), payload)
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static huddle_info = async (token: string) => {
    try {
      return await (
        await axios.get(ApiConstants.AUTH_API.GET_HUDDLE_INFO.URL(), {
          headers: { Authorization: `Bearer ${token}` },
        })
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static ms_login = async (id: string, uri: string) => {
    try {
      return await (
        await axios.get(ApiConstants.AUTH_API.MS_LOGIN.URL(id, uri))
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static ms_team_login = async () => {
    try {
      return await (
        await axios.get(ApiConstants.AUTH_API.MS_TEAM_LOGIN.URL())
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static ms_team_events = async (
    code: string,
    email: string,
    isExistingAccessTokenValid: boolean,
  ) => {
    try {
      return await (
        await axios.get(
          ApiConstants.AUTH_API.MS_TEAM_EVENTS.URL(code, email, isExistingAccessTokenValid),
        )
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static google_auth = async (code: string, redirectUri: string) => {
    try {
      return await (
        await axios.get(ApiConstants.AUTH_API.GOOGLE_AUTHCALLBACK.URL(code, redirectUri))
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static zoom_auth = async (code: string, redirectUri: string) => {
    try {
      return await (
        await axios.get(ApiConstants.AUTH_API.ZOOM_AUTHCALLBACK.URL(code, redirectUri))
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static google_refresh = async () => {
    try {
      return await (
        await axios.get(ApiConstants.AUTH_API.GOOGLE_REFRESH.URL())
      )?.data;
    } catch (err) {
      throw err;
    }
  };
  static logout = async ({
    accessToken,
    isMSLogin,
  }: {
    accessToken: string;
    isMSLogin: boolean;
  }) => {
    return await (
      await axios.post(ApiConstants.AUTH_API.LOGOUT.URL(), { accessToken, isMSLogin })
    ).data;
  };
}
